import { createLogger } from "@/lib/logger";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { TranscriptSegment } from "../../models/podcast";

const logger = createLogger("PlayerContext");

// Helper function to safely revoke blob URLs
const safeRevokeObjectURL = (url: string | undefined, context: string) => {
  try {
    if (url && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    logger.warn(`Error revoking blob URL in ${context}:`, error);
  }
};

interface PlayerContextType {
  isPlaying: boolean; // Is sequential TTS playback active?
  currentTime: number; // Start time of the current segment being played
  duration: number; // Total duration based on last segment's end time
  currentSegment: TranscriptSegment | undefined; // Segment currently playing via TTS
  segments: TranscriptSegment[];
  togglePlay: () => void; // Start/stop sequential TTS
  seekToSegment: (segmentIndex: number) => void; // Seek to a specific segment index
  skipForward: () => void; // Go to next segment
  skipBackward: () => void; // Go to previous segment
  // setPlaybackRate: (rate: number) => void; // Playback rate for TTS might be complex/costly - remove for now
  volume: number;
  setVolume: (volume: number) => void;
  setSegments: (segments: TranscriptSegment[]) => void; // Load the transcript segments
  playSingleSegmentTTS: (segmentIndex: number) => Promise<void>; // Play only one segment on demand
  isTTSLoading: boolean; // Indicate if TTS audio is currently being fetched/loaded
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error("usePlayer must be used within a PlayerProvider");
  }
  return context;
};

export const PlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false); // Is sequential playback active?
  const [currentTime, setCurrentTime] = useState(0); // Start time of current segment
  const [duration, setDuration] = useState(0); // Total duration
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [currentSegment, setCurrentSegment] = useState<TranscriptSegment | undefined>(undefined);
  const [currentTTSIndex, setCurrentTTSIndex] = useState<number>(-1); // Index of segment being played/loaded
  const [volume, setVolume] = useState(1);
  const [isTTSLoading, setIsTTSLoading] = useState(false); // Loading indicator

  const ttsAudioRef = useRef<HTMLAudioElement | null>(null); // Ref for the single TTS audio element
  const isPlayingRef = useRef(isPlaying); // Ref to track isPlaying state in callbacks
  const currentTTSIndexRef = useRef(currentTTSIndex); // Ref to track index in callbacks

  // Update refs when state changes
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    currentTTSIndexRef.current = currentTTSIndex;
  }, [currentTTSIndex]);

  // Cleanup TTS audio on unmount or when stopping
  useEffect(() => {
    return () => {
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
        // Safely revoke blob URL
        safeRevokeObjectURL(ttsAudioRef.current.src, "unmount");
        ttsAudioRef.current = null;
      }
    };
  }, []);

  // Update duration and reset state when segments change
  useEffect(() => {
    if (segments.length > 0) {
      setDuration(segments[segments.length - 1]?.endTime || 0);
      setCurrentTime(0);
      setCurrentSegment(undefined);
      setCurrentTTSIndex(-1);
      setIsPlaying(false);
      setIsTTSLoading(false);
      // Stop any ongoing TTS
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
        // Safely revoke blob URL
        safeRevokeObjectURL(ttsAudioRef.current.src, "segments change");
        ttsAudioRef.current = null; // Ensure ref is cleared
      }
    } else {
      setDuration(0);
    }
  }, [segments]);

  // Update currentSegment and currentTime based on currentTTSIndex
  useEffect(() => {
    const segment = segments[currentTTSIndex];
    if (segment) {
      setCurrentSegment(segment);
      setCurrentTime(segment.startTime);
    } else {
      // If index is out of bounds (e.g., -1 or past the end), reset
      setCurrentSegment(undefined);
      setCurrentTime(0);
      // If playback was active and we reached the end, stop it
      if (isPlayingRef.current && currentTTSIndex >= segments.length) {
        setIsPlaying(false);
      }
    }
  }, [currentTTSIndex, segments]);

  // Update TTS audio volume when context volume changes
  useEffect(() => {
    if (ttsAudioRef.current) {
      ttsAudioRef.current.volume = volume;
    }
  }, [volume]);

  // --- Core TTS Playback Logic ---
  const _playTTSForIndex = async (index: number, playSequentially: boolean) => {
    if (index < 0 || index >= segments.length) {
      logger.info("Index out of bounds, stopping playback.");
      setIsPlaying(false); // Stop sequential playback if index is invalid
      setIsTTSLoading(false);
      setCurrentTTSIndex(segments.length); // Set index past the end to indicate completion
      return;
    }

    const segmentToPlay = segments[index];
    if (!segmentToPlay || !segmentToPlay.text) {
      logger.info(`Segment ${index} has no text, skipping.`);
      // If playing sequentially, immediately try the next one
      if (playSequentially && isPlayingRef.current) {
        setCurrentTTSIndex(index + 1); // Move to next index state
        _playTTSForIndex(index + 1, true); // Recursively call for next
      } else {
        setIsTTSLoading(false);
      }
      return;
    }

    setIsTTSLoading(true);
    setCurrentTTSIndex(index); // Update state to reflect the segment being loaded/played

    // Stop and clean up any currently playing TTS audio
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      // Safely revoke blob URL
      safeRevokeObjectURL(ttsAudioRef.current.src, "_playTTSForIndex");
      ttsAudioRef.current = null;
    }

    try {
      // Include speaker information in the request body
      const response = await fetch("/api/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: segmentToPlay.text,
          speaker: segmentToPlay.speaker || "alloy", // Default voice if speaker is undefined
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`TTS API request failed for segment ${index}: ${response.statusText}`);
      }

      const audioBlob = await response.blob();
      const ttsUrl = URL.createObjectURL(audioBlob);

      ttsAudioRef.current = new Audio(ttsUrl);
      ttsAudioRef.current.volume = volume;

      // --- Playback Event Handlers ---
      const handlePlay = () => {
        setIsTTSLoading(false); // Loading finished when playback starts
      };

      const handleEnded = () => {
        // Safely revoke blob URL
        safeRevokeObjectURL(ttsUrl, "handleEnded");
        ttsAudioRef.current = null; // Clear the ref
        setIsTTSLoading(false);

        // If sequential playback is active, play the next segment
        if (playSequentially && isPlayingRef.current) {
          const nextIndex = currentTTSIndexRef.current + 1;
          if (nextIndex < segments.length) {
            setCurrentTTSIndex(nextIndex); // Trigger state update for next segment
            _playTTSForIndex(nextIndex, true); // Play next
          } else {
            logger.info("Finished sequential playback.");
            setIsPlaying(false); // Reached the end
            setCurrentTTSIndex(segments.length); // Indicate completion
          }
        } else if (!playSequentially) {
          // If only playing a single segment, reset index indicator after finishing
          // Avoid resetting if sequential playback was just stopped
          if (!isPlayingRef.current) {
            setCurrentTTSIndex(-1);
          }
        }
      };

      const handleError = (e: Event | string) => {
        logger.error(`Error playing TTS for segment ${index}:`, e);
        // Safely revoke blob URL
        safeRevokeObjectURL(ttsUrl, "handleError");
        ttsAudioRef.current = null;
        setIsTTSLoading(false);
        // Stop sequential playback on error
        if (playSequentially) {
          setIsPlaying(false);
        }
        setCurrentTTSIndex(-1); // Reset index on error
      };

      ttsAudioRef.current.addEventListener("play", handlePlay);
      ttsAudioRef.current.addEventListener("ended", handleEnded);
      ttsAudioRef.current.addEventListener("error", handleError);
      // --- End Playback Event Handlers ---

      try {
        await ttsAudioRef.current.play();
      } catch (playError) {
        // Handle audio play permission errors
        if (playError instanceof DOMException) {
          if (playError.name === "NotAllowedError") {
            logger.warn(`Audio playback blocked - user interaction required for segment ${index}`);
            // You could show a toast notification here if desired
          } else if (playError.name === "AbortError") {
            logger.warn(`Audio playback aborted for segment ${index}`);
          } else {
            logger.error(`Audio playback failed for segment ${index}:`, playError);
          }
        } else {
          logger.error(`Unexpected audio playback error for segment ${index}:`, playError);
        }

        // Clean up on play error
        setIsTTSLoading(false);
        if (playSequentially) {
          setIsPlaying(false);
        }
        setCurrentTTSIndex(-1);

        // Clean up the audio element
        if (ttsAudioRef.current) {
          safeRevokeObjectURL(ttsAudioRef.current.src, "play error");
          ttsAudioRef.current = null;
        }
        return; // Exit early on play error
      }
    } catch (error) {
      logger.error(`Error fetching TTS for segment ${index}:`, error);
      setIsTTSLoading(false);
      // Stop sequential playback on fetch error
      if (playSequentially) {
        setIsPlaying(false);
      }
      setCurrentTTSIndex(-1); // Reset index on error
    }
  };

  // --- Context Functions ---

  const setSegmentsWrapper = (newSegments: TranscriptSegment[]) => {
    // This function now primarily relies on the useEffect hook triggered by segment changes
    setSegments(newSegments);
  };

  const playSingleSegmentTTS = async (segmentIndex: number) => {
    if (isPlayingRef.current) {
      // If sequential play is active, stop it first
      setIsPlaying(false);
    }
    // Stop any currently playing TTS before starting the new one
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      safeRevokeObjectURL(ttsAudioRef.current.src, "playSingleSegmentTTS");
      ttsAudioRef.current = null;
    }
    setCurrentTTSIndex(segmentIndex); // Set index immediately for visual feedback
    await _playTTSForIndex(segmentIndex, false); // Play just this one
  };

  const togglePlay = () => {
    const currentlyPlaying = isPlayingRef.current;
    const newIsPlaying = !currentlyPlaying;
    setIsPlaying(newIsPlaying); // Update state first

    if (newIsPlaying) {
      // Start playing
      let startIndex = currentTTSIndexRef.current;
      // If stopped at the end or beginning, start from 0
      if (startIndex < 0 || startIndex >= segments.length) {
        startIndex = 0;
      }
      setCurrentTTSIndex(startIndex); // Ensure state reflects starting point
      _playTTSForIndex(startIndex, true); // Start sequential playback
    } else {
      // Stop playing
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
        // Don't revoke URL here, allow seeking/restarting
      }
      setIsTTSLoading(false); // Ensure loading indicator is off
    }
  };

  const seekToSegment = (segmentIndex: number) => {
    if (segmentIndex < 0 || segmentIndex >= segments.length) return;

    const wasPlaying = isPlayingRef.current;
    if (wasPlaying) {
      setIsPlaying(false); // Temporarily stop playback state
    }

    // Stop current audio
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      safeRevokeObjectURL(ttsAudioRef.current.src, "seekToSegment");
      ttsAudioRef.current = null;
    }

    setCurrentTTSIndex(segmentIndex); // Set the new index

    // If it was playing before seeking, restart playback from the new index
    if (wasPlaying) {
      setIsPlaying(true); // Set playing state back
      _playTTSForIndex(segmentIndex, true);
    } else {
      // If it wasn't playing, just update the current time/segment display
      const segment = segments[segmentIndex];
      if (segment) {
        setCurrentSegment(segment);
        setCurrentTime(segment.startTime);
      }
      setIsTTSLoading(false); // Ensure loading is off
    }
  };

  const skipForward = () => {
    let nextIndex = currentTTSIndexRef.current + 1;
    if (nextIndex >= segments.length) {
      nextIndex = segments.length - 1; // Stop at the last segment
      if (isPlayingRef.current) {
        // If playing, stop at the end
        setIsPlaying(false);
        if (ttsAudioRef.current) ttsAudioRef.current.pause();
        setCurrentTTSIndex(segments.length); // Indicate end
        setIsTTSLoading(false);
      }
      return;
    }
    seekToSegment(nextIndex);
  };

  const skipBackward = () => {
    let prevIndex = currentTTSIndexRef.current - 1;
    if (prevIndex < 0) {
      prevIndex = 0; // Stop at the first segment
    }
    seekToSegment(prevIndex);
  };

  // --- End Context Functions ---

  const value: PlayerContextType = {
    // Ensure type matching
    isPlaying,
    currentTime,
    duration,
    currentSegment,
    segments,
    togglePlay,
    seekToSegment,
    skipForward,
    skipBackward,
    volume,
    setVolume,
    setSegments: setSegmentsWrapper,
    playSingleSegmentTTS,
    isTTSLoading,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};
